/**
 * Phase 10 R5 — useDayPartsLines (BATCHED)
 *
 * Single-query day-scoped fetch of parts_lines for every ticket scheduled
 * for `dateStr` in the active shop. Used exclusively by
 * `useDailyFinancialSnapshot`.
 *
 * HARD CONTRACT: exactly **one** Supabase query per invocation, regardless
 * of how many tickets the day has. Never call per-ticket fetchers in a loop.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { PartsLine } from '@/hooks/parts/usePartsLines';

export function useDayPartsLines(dateStr: string | undefined, enabled = true) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery<PartsLine[]>({
    queryKey: ['day-parts-lines', shopId, dateStr],
    enabled: !!shopId && !!dateStr && enabled,
    queryFn: async () => {
      // Single query: parts_lines joined to tickets via ticket_id, filtered
      // by shop + the day's scheduled_date. We use an inner-style filter via
      // tickets!inner so PostgREST gets the day scope without a second round
      // trip. Result is flat parts_lines rows.
      const { data, error } = await (supabase as any)
        .from('parts_lines')
        .select('*, tickets!inner(scheduled_date, shop_id)')
        .eq('shop_id', shopId)
        .eq('tickets.shop_id', shopId)
        .eq('tickets.scheduled_date', dateStr)
        .is('deleted_at', null);
      if (error) throw error;
      // Strip the joined ticket column to keep PartsLine shape.
      return ((data ?? []) as any[]).map(({ tickets: _t, ...rest }) => rest as PartsLine);
    },
  });
}
